import {
  Component, OnInit, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormBuilder, FormGroup, Validators, ReactiveFormsModule
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../shared/services/user.service';
import { AuthService } from '../../core/services/auth.service';
import { BusinessContextService } from '../../core/services/business-context.service';
import { ModuleService } from '../../shared/services/module.service';
import { NavigationService } from '../../core/services/navigation.service';
import { PermissionModule } from '../../shared/models';
import { AppButton } from '../../shared/components/app-button/app-button';
import { AppInput } from '../../shared/components/app-input/app-input';
import { LoadingService } from '../../core/services/loading.service';
import { AppSectionHeader } from "../../shared/components/app-section-header/app-section-header";

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppButton, AppInput, AppSectionHeader],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styles: [`
    .compact-section {
      background: var(--sidebar-bg);
      border: 1px solid var(--border-color);
      border-radius: 1rem;
      padding: 1.25rem;
      box-shadow: 0 2px 8px -2px rgba(0,0,0,0.04);
    }
    .permission-tile {
      background: var(--sidebar-bg);
      border: 1px solid var(--border-color);
      border-radius: 0.75rem;
      padding: 0.75rem;
      transition: all 0.2s ease;
    }
    .permission-tile:hover {
      border-color: var(--primary);
      opacity: 0.9;
    }
    .status-chip {
      padding: 0.35rem 0.6rem;
      border-radius: 0.5rem;
      font-size: 8px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      border: 1px solid transparent;
      display: flex;
      align-items: center;
      gap: 0.4rem;
    }
    .status-chip.active {
      background: var(--color-primary);
      color: white;
      box-shadow: 0 4px 12px rgba(var(--color-primary-rgb), 0.3);
    }
    .status-chip:not(.active) {
      background: rgba(var(--color-foreground), 0.03);
      color: rgba(var(--color-foreground), 0.5);
      border-color: rgba(var(--color-foreground), 0.05);
    }
    .status-chip:not(.active):hover {
      background: rgba(var(--color-foreground), 0.06);
      color: rgba(var(--color-foreground), 0.7);
    }
  `],
  template: `
    <div class="w-full animate-fade-in pb-8">
        <!-- Header Shared Component -->
    <app-section-header [title]="isEdit() ? 'Ficha de Operador' : (isMaestroForm() ? 'Alta Core System' : 'Invitación Colaborador')"
        [description]="'Maestro' +' | ' + (isEdit() ? 'Perfil' : 'Nuevo') + ' | ' + (isMaestroForm() ? 'Infraestructura Maestro' : businessName())" icon="mdi:puzzle-outline"
        [showBack]="true">
    </app-section-header>
      <div class="animate-fade-in space-y-3">
        
        <form [formGroup]="activeForm()" class="space-y-3">
          <!-- Identidad x2 Columnas -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            
            <div class="compact-section space-y-3">
              <p class="flex items-center gap-1.5 text-[8px] font-black uppercase tracking-[0.2em] text-foreground/60">
                <iconify-icon icon="mdi:card-text-outline" width="11" class="text-primary"></iconify-icon>
                Identidad Operativa
              </p>
              
              <app-input label="Nombre Completo" formControlName="fullName" size="sm" icon="mdi:account" [required]="true" placeholder="Nombre completo"></app-input>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <app-input label="Correo de Acceso" formControlName="email" type="email" size="sm" icon="mdi:email" [required]="true" placeholder="usuario@dentium.com"></app-input>
                <app-input label="Nombre de Usuario" formControlName="username" size="sm" icon="mdi:account-badge-outline" placeholder="ej: jdoe"></app-input>
              </div>
              
              <div *ngIf="isNewUser()" class="p-3 bg-primary/[0.01] border border-primary/5 rounded-lg">
                 <app-input label="Contraseña" formControlName="password" type="password" size="sm" icon="mdi:shield-lock-outline" [showToggle]="true" helperText="Seguridad inicial"></app-input>
              </div>
            </div>

            <div class="compact-section space-y-4">
              <p class="flex items-center gap-1.5 text-[8px] font-black uppercase tracking-[0.2em] text-foreground/60">
                <iconify-icon icon="mdi:security" width="11" class="text-primary"></iconify-icon>
                Seguridad y Estado de Acceso
              </p>

              <!-- Custom Status Selector (Premium) -->
              <div class="p-3 bg-foreground/[0.01] border border-foreground/[0.03] rounded-lg space-y-2">
                 <div class="flex items-center justify-between mb-1">
                    <span class="text-[9px] font-bold text-slate-800 dark:text-white/90/50 uppercase tracking-widest">Acceso Global Sistema</span>
                    <span class="text-[7px] font-bold text-foreground/45 italic">Seleccione nivel actual</span>
                 </div>
                 <div class="flex gap-1.5">
                    <div *ngFor="let s of statusOptions" 
                         (click)="updateStatus(s.key)"
                         class="status-chip"
                         [class.active]="activeForm().get('status')?.value === s.key">
                       <iconify-icon [icon]="s.icon" width="10"></iconify-icon>
                       {{ s.label }}
                    </div>
                 </div>
              </div>

              <!-- Toggles y Ayudas -->
              <div class="space-y-2">
                <div class="flex flex-col gap-1 p-2.5 bg-foreground/[0.01] border border-foreground/[0.03] rounded-lg">
                   <label class="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity">
                      <span class="text-[9px] font-bold text-slate-800 dark:text-white/90/50 uppercase tracking-widest">Atributo Maestro (SuperAdmin)</span>
                      <div class="relative">
                         <input type="checkbox" formControlName="isSuperAdmin" class="sr-only peer">
                         <div class="w-7 h-3.5 bg-foreground/10 rounded-full peer peer-checked:bg-amber-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-2.5 after:w-2.5 after:transition-all peer-checked:after:translate-x-3.5 shadow-sm"></div>
                      </div>
                   </label>
                   <p class="text-[7px] text-foreground/60 italic font-medium leading-tight mt-1 max-w-[280px]">
                      Habilita la administración total de la plataforma (Negocios, Facturación Global y Usuarios Maestro).
                   </p>
                </div>

                <label class="p-2.5 bg-foreground/[0.01] border border-foreground/[0.03] rounded-lg flex items-center justify-between cursor-pointer hover:bg-foreground/[0.02] transition-all group">
                   <div class="flex flex-col gap-0.5">
                      <span class="text-[9px] font-bold text-slate-800 dark:text-white/90/50 uppercase tracking-widest">Visibilidad General</span>
                      <span class="text-[7px] text-foreground/45 italic">Activación lógica de la cuenta</span>
                   </div>
                   <div class="relative">
                      <input type="checkbox" formControlName="isActive" class="sr-only peer">
                      <div class="w-7 h-3.5 bg-foreground/10 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-2.5 after:w-2.5 after:transition-all peer-checked:after:translate-x-3.5"></div>
                   </div>
                </label>
              </div>
            </div>
          </div>
        </form>

        <!-- Matriz de Permisos (Filtrada por Contexto) -->
        <div class="compact-section space-y-3">
           <div class="flex items-center justify-between">
              <div class="flex flex-col">
                 <p class="flex items-center gap-1.5 text-[8px] font-black uppercase tracking-[0.2em] text-foreground/60">
                   <iconify-icon icon="mdi:shield-key-outline" width="11" class="text-primary"></iconify-icon>
                   Permisos del Módulo de Sistema
                 </p>
                 <span class="text-[7px] text-foreground/45 italic mt-0.5 font-bold">Defina los alcances para este operador</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-[7px] font-black uppercase tracking-tight text-foreground/45 mr-1">{{ selectedActionCount() }} permisos activos</span>
                <app-button variant="glass" size="xs" (click)="clearBizActions()" class="h-6 px-2 text-[7px] font-black uppercase">Limpiar</app-button>
                <app-button variant="glass" size="xs" (click)="selectAllBizActions()" class="h-6 px-2 text-[7px] font-black uppercase text-primary">Todos los Permitidos</app-button>
              </div>
           </div>

           <!-- Grid Compacto -->
           <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
              <div *ngFor="let mod of availableModules()" 
                   class="permission-tile space-y-2.5 border-l-2"
                   [style.border-color]="isModuleEnabled(mod) ? 'var(--color-primary)' : 'transparent'">
                 
                 <label class="flex items-center justify-between cursor-pointer group">
                    <div class="flex items-center gap-2 overflow-hidden">
                       <iconify-icon [attr.icon]="mod.icon" width="14" class="text-primary/70 shrink-0"></iconify-icon>
                       <span class="text-[9px] font-black uppercase tracking-widest text-foreground/70 truncate group-hover:text-primary transition-colors">{{ mod.label }}</span>
                    </div>
                    <input type="checkbox" [checked]="isModuleEnabled(mod)" (change)="toggleBizModule(mod)" class="w-3.5 h-3.5 rounded border-foreground/10 text-primary focus:ring-primary/20 accent-primary cursor-pointer shrink-0">
                 </label>
                 <div class="h-px bg-foreground/[0.04]"></div>
                 <div class="flex flex-wrap gap-1.5">
                    <button *ngFor="let action of mod.actions" 
                            type="button"
                            [disabled]="action.default"
                            (click)="toggleBizAction(action.full)"
                            class="px-2 py-1 rounded text-[7px] font-black uppercase tracking-tight transition-all border shrink-0"
                            [class.bg-primary/5]="isBizActionSelected(action.full)"
                            [class.border-primary/20]="isBizActionSelected(action.full)"
                            [class.text-primary]="isBizActionSelected(action.full)"
                            [class.bg-foreground/[0.02]]="!isBizActionSelected(action.full)"
                            [class.border-transparent]="!isBizActionSelected(action.full)"
                            [class.text-foreground/60]="!isBizActionSelected(action.full)">
                       {{ action.label }}
                    </button>
                 </div>
              </div>
           </div>
        </div>

        <!-- Footer Footer -->
        <div class="flex items-center justify-end gap-2 pt-2">
           <div *ngIf="error()" class="mr-auto text-[8px] font-black text-red-500 uppercase tracking-widest bg-red-500/5 px-3 py-1.5 rounded-lg border border-red-500/10 animate-shake">
              {{ error() }}
           </div>
           <app-button variant="outline" size="xs" (click)="goBack()" class="h-8 text-[9px] font-black uppercase px-5">Cancelar</app-button>
           <app-button (click)="isMaestroForm() ? saveSuperAdmin() : inviteUser()" 
                       [disabled]="activeForm().invalid || saving()"
                       [loading]="saving()"
                       variant="primary" size="xs" 
                       class="h-8 text-[9px] font-black uppercase px-6 shadow-lg shadow-primary/20"
                       [iconLeft]="saving() ? 'mdi:loading' : 'mdi:check'">
              {{ saving() ? 'Guardando...' : (isEdit() ? 'Actualizar Registro' : 'Enviar Invitación') }}
           </app-button>
        </div>
      </div>
    </div>
  `,
})
export class UserForm implements OnInit {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  public bizCtx = inject(BusinessContextService);
  private moduleService = inject(ModuleService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private nav = inject(NavigationService);
  private loadingService = inject(LoadingService);

  paramsSignal = toSignal(this.route.params);
  querySignal = toSignal(this.route.queryParams);

  allDbModules = signal<any[]>([]);

  statusOptions = [
    { key: 'active', label: 'Permitido', icon: 'solar:verified-check-bold' },
    { key: 'suspended', label: 'Suspendido', icon: 'solar:danger-bold' },
    { key: 'pending', label: 'Pendiente', icon: 'solar:clock-circle-bold' },
  ];

  get userIdParam(): string | null {
    const params = this.paramsSignal() as any;
    return params?.['id'] || null;
  }

  // Detección de negocio desde la ruta (si estuviera anidado) o contexto
  get businessFromRoute(): string | null {
    const params = this.paramsSignal() as any;
    const qParams = this.querySignal() as any;
    if (!params) return null;

    // Si la ruta contiene /business/edit/ el parámetro 'id' siempre es el negocio
    if (this.router.url.includes('/business/edit/')) {
      return params['id'];
    }
    return qParams?.['business'] || this.bizCtx.business() || null;
  }

  saving = signal(false);
  error = signal('');
  isEdit = signal(false);
  isNewUser = signal(true);

  readonly isMaestroForm = computed(() => this.authService.currentUserValue?.isSuperAdmin === true);
  readonly businessName = computed(() => this.bizCtx.businessName());

  readonly availableModules = computed<PermissionModule[]>(() => {
    const dbModules = this.allDbModules();
    const allowed = this.bizCtx.allowedActions() || [];
    const isMaestro = this.isMaestroForm();

    const all: PermissionModule[] = dbModules.map(m => {
      const firstPerm = (m.permissions && m.permissions.length > 0) ? m.permissions[0] : '';
      const prefix = firstPerm.includes(':') ? firstPerm.split(':')[0] : m.key;

      return {
        module: m.key,
        prefix: prefix,
        label: m.label,
        icon: m.icon,
        group: m.group,
        actions: (m.permissions || []).map((p: string) => {
          const actionKey = p.split(':')[1] || p;
          const labels: Record<string, string> = {
            list: 'Listar', create: 'Registrar', edit: 'Editar',
            delete: 'Eliminar', export: 'Exportar', print: 'Imprimir',
            sync: 'Sincronizar', manage: 'Gestionar',
            renew: 'Renovar', assign: 'Asignar', view: 'Ver',
            odontogram: 'Odontograma', sri: 'Facturación SRI'
          };
          return {
            key: actionKey,
            full: p,
            label: labels[actionKey] || actionKey.charAt(0).toUpperCase() + actionKey.slice(1),
            default: actionKey === 'list'
          };
        })
      };
    });

    if (isMaestro) {
      return all.filter(m => m.group === 'system');
    }

    if (allowed.includes('*')) return all;

    return all
      .filter(mod => mod.actions.some((a: any) => allowed.includes(`${mod.module}:${a.key}`)))
      .map(mod => ({
        ...mod,
        actions: mod.actions.filter((a: any) => allowed.includes(`${mod.module}:${a.key}`))
      }));
  });

  selectedBizActions = signal<string[]>([]);
  readonly selectedActionCount = computed(() => this.selectedBizActions().length);

  saForm: FormGroup;
  bizForm: FormGroup;

  activeForm = computed(() => this.isMaestroForm() ? this.saForm : this.bizForm);

  constructor() {
    this.saForm = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      username: [''],
      password: ['', Validators.minLength(6)],
      isSuperAdmin: [false],
      isActive: [true],
      status: ['active'],
    });

    this.bizForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      fullName: ['', Validators.required],
      username: [''],
      password: ['', [Validators.required, Validators.minLength(6)]],
      isActive: [true],
      status: ['active'],
    });
  }

  goBack() {
    this.nav.goBack();
  }

  ngOnInit() {
    this.loadModules();
    const id = this.userIdParam;
    if (id) {
      this.isEdit.set(true);
      this.isNewUser.set(false);
      this.loadUser(id);
    }
  }

  private loadModules() {
    this.moduleService.loadAll({ all: this.isMaestroForm(), limit: 100 }).subscribe({
      next: (res) => this.allDbModules.set(res.data || [])
    });
  }

  updateStatus(status: string) {
    this.activeForm().get('status')?.setValue(status);
  }

  private loadUser(id: string) {
    this.loadingService.showLoading('Cargando Usuario', 'Obteniendo perfil del operador...');
    this.userService.getById(id).subscribe({
      next: (resp: any) => {
        const user = resp?.data ?? resp;
        this.saForm.patchValue({
          fullName: user.fullName,
          email: user.email,
          username: user.username || '',
          isSuperAdmin: user.isSuperAdmin,
          isActive: user.isActive,
          status: user.status || 'active'
        });
        this.bizForm.patchValue({
          fullName: user.fullName,
          email: user.email,
          username: user.username || '',
          isActive: user.isActive,
          status: user.status || 'active'
        });

        if (id) {
          this.saForm.get('password')?.clearValidators();
          this.saForm.get('password')?.updateValueAndValidity();
          this.bizForm.get('password')?.clearValidators();
          this.bizForm.get('password')?.updateValueAndValidity();
        }

        if (user.permissions && user.permissions.length > 0) {
          this.selectedBizActions.set([...user.permissions]);
        } else if (user.Staff?.customPermissions) {
          this.selectedBizActions.set([...user.Staff.customPermissions]);
        }
        this.loadingService.hideLoading();
      },
      error: () => {
        this.error.set('Error al recuperar información');
        this.loadingService.hideLoading();
      },
    });
  }

  isModuleEnabled(mod: any): boolean {
    if (this.selectedBizActions().includes('*')) return true;
    return mod.actions.length > 0 && mod.actions.every((a: any) => this.selectedBizActions().includes(a.full));
  }
  isBizActionSelected(full: string): boolean {
    return this.selectedBizActions().includes(full);
  }
  toggleBizModule(mod: PermissionModule) {
    let current = this.selectedBizActions().filter(a => a !== '*');
    const modPerms = mod.actions.map((a: any) => a.full);
    const isAllSelected = modPerms.every((a: any) => current.includes(a));

    if (isAllSelected) {
      this.selectedBizActions.set(current.filter(a => !modPerms.includes(a)));
    } else {
      this.selectedBizActions.set([...new Set([...current, ...modPerms])]);
    }
  }
  toggleBizAction(perm: string) {
    const allowed = this.isMaestroForm() ? ['*'] : this.bizCtx.allowedActions();
    if (!allowed.includes('*') && !allowed.includes(perm)) return;

    // Quitar '*' al tocar cualquier acción específica
    let current = this.selectedBizActions().filter(a => a !== '*');

    this.selectedBizActions.set(
      current.includes(perm) ? current.filter(a => a !== perm) : [...current, perm]
    );
  }
  selectAllBizActions() {
    const all = this.availableModules().flatMap(mod =>
      mod.actions.map((a: any) => a.full)
    );
    this.selectedBizActions.set([...new Set(all)]);
  }
  clearBizActions() {
    this.selectedBizActions.set([]);
  }
  saveSuperAdmin() {
    if (this.saForm.invalid) return;
    this.saving.set(true);
    const id = this.userIdParam;

    const payload: any = {
      ...this.saForm.value,
      permissions: this.selectedBizActions()
    };

    const req = id ? this.userService.update(id, payload) : this.userService.create(payload);
    req.subscribe({
      next: () => { this.saving.set(false); this.goBack(); },
      error: (err: any) => { this.saving.set(false); this.error.set(err?.error?.message ?? 'Error al guardar'); }
    });
  }
  inviteUser() {
    if (this.bizForm.invalid) return;
    if (this.selectedBizActions().length === 0) {
      this.error.set('Debe seleccionar al menos un permiso para el colaborador');
      return;
    }
    this.saving.set(true);
    const val = this.bizForm.value;
    const id = this.userIdParam;
    const payload: any = {
      ...val,
      permissions: this.selectedBizActions(),
      business: this.businessFromRoute || undefined
    };
    const req = id ? this.userService.update(id, payload) : this.userService.invite(payload as any);
    req.subscribe({
      next: () => { this.saving.set(false); this.goBack(); },
      error: (err: any) => { this.saving.set(false); this.error.set(err?.error?.message ?? 'Error al invitar'); }
    });
  }
}
